// src/pages/ExamTerm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

function formatPL(dt) {
    try {
        return new Intl.DateTimeFormat('pl-PL', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(dt));
    } catch { return dt; }
}

// do input[type="datetime-local"] (bez strefy)
function toLocalInputValue(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
}

// z input[type="datetime-local"] do ISO (UTC)
function fromLocalInputValue(localStr) {
    if (!localStr) return null;
    const d = new Date(localStr);
    const off = d.getTimezoneOffset();
    const utc = new Date(d.getTime() + off * 60000);
    return utc.toISOString();
}

export default function ExamTerm() {
    const { id } = useParams(); // term_id
    const nav = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [err, setErr] = useState('');
    const [okMsg, setOkMsg] = useState('');

    const [term, setTerm] = useState(null);

    // JOIN CODE — pobierany z backendu (tak samo jak dla grup)
    const [joinCode, setJoinCode] = useState('');
    const [joinErr, setJoinErr] = useState('');
    const [copyOk, setCopyOk] = useState('');

    // Formularz
    const [form, setForm] = useState({
        exam_id: '',
        title: '',
        group_id: '',
        group_name: '',
        group_year: '',
        field_of_study: '',
        study_semester: '',
        study_mode: '',
        exam_datetime: '',
        exam_duration: 90,
        proctoring: 0,
        level: 'brak',
    });

    // Modal potwierdzenia usunięcia
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    // Odświeżanie czasu do startu
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);


    const toIsoFromLocalInput = (localVal) => {
        if (!localVal) return null;
        const [d, t] = localVal.split('T');
        const [hh, mm, ss = '00'] = t.split(':');
        return `${d} ${hh}:${mm}:${ss}`;
    };

    // Pobranie danych terminu
    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setErr('');
            setOkMsg('');
            try {
                const r = await fetch(`/api/prowadzacy/termin/${id}`, {
                    credentials: 'include',
                    headers: { ...authHeader() },
                });
                if (!r.ok) {
                    const j = await r.json().catch(() => ({}));
                    throw new Error(j?.blad || `Błąd ${r.status}`);
                }
                const data = await r.json();
                if (!mounted) return;

                setTerm(data);
                setForm({
                    exam_id: data.exam_id,
                    title: data.title || '',
                    group_id: data.group_id,
                    group_name: data.group_name || '',
                    group_year: data.group_year || '',
                    field_of_study: data.field_of_study || '',
                    study_semester: data.study_semester || '',
                    study_mode: data.study_mode || '',
                    exam_datetime: toLocalInputValue(data.exam_datetime),
                    exam_duration: Number(data.exam_duration || 90),
                    proctoring: data.proctoring ? 1 : 0,
                    level: data.level || 'brak',
                });
            } catch (e) {
                if (mounted) setErr(e.message || 'Błąd pobierania terminu');
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => { mounted = false; };
    }, [id]);

    // Pobierz kod dołączenia z backendu
    useEffect(() => {
        let cancelled = false;
        async function loadJoinCode() {
            setJoinErr(''); setJoinCode(''); setCopyOk('');
            try {
                const r = await fetch(`/api/prowadzacy/termin/${id}/join-code`, {
                    credentials: 'include',
                    headers: { ...authHeader() },
                });
                if (!r.ok) {
                    const j = await r.json().catch(() => ({}));
                    throw new Error(j?.blad || `Błąd pobierania kodu (${r.status})`);
                }
                const j = await r.json();
                if (!cancelled) setJoinCode(String(j?.code || ''));
            } catch (e) {
                if (!cancelled) setJoinErr(e.message || 'Nie udało się pobrać kodu');
            }
        }
        loadJoinCode();
        return () => { cancelled = true; };
    }, [id]);

    const startUtcMs = useMemo(() => term ? new Date(term.exam_datetime).getTime() : null, [term]);
    const lockMs = useMemo(() => (startUtcMs ? startUtcMs - 15 * 60 * 1000 : null), [startUtcMs]);

    const isLocked = useMemo(() => {
        if (!lockMs) return false;
        return now >= lockMs;
    }, [now, lockMs]);

    const canGoToRun = useMemo(() => {
        if (!lockMs) return false;
        return now >= lockMs;
    }, [now, lockMs]);

    const leftToStart = useMemo(() => {
        if (!startUtcMs) return null;
        const diff = startUtcMs - now;
        const s = Math.max(0, Math.ceil(diff / 1000));
        const hh = Math.floor(s / 3600);
        const mm = Math.floor((s % 3600) / 60);
        const ss = s % 60;
        return `${hh > 0 ? hh + ':' : ''}${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    }, [startUtcMs, now]);

    function setField(k, v) { setForm(prev => ({ ...prev, [k]: v })); }

    async function onSave(e) {
        e?.preventDefault?.();
        setErr(''); setOkMsg(''); setSaving(true);
        try {
            const payload = {
                exam_id: Number(form.exam_id),
                group_id: Number(form.group_id),
                exam_datetime: toIsoFromLocalInput(form.exam_datetime),
                exam_duration: Number(form.exam_duration),
                proctoring: !!form.proctoring,
                level: form.level || 'brak',
            };

            const r = await fetch(`/api/prowadzacy/termin/${id}`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify(payload),
            });
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j?.blad || `Błąd zapisu (${r.status})`);
            }

            const j = await r.json().catch(() => ({}));
            const updated = j?.term ?? j;

            let finalTerm = updated;
            const looksIncomplete = !updated || !updated.exam_datetime || updated.term_id == null;
            if (looksIncomplete) {
                const g = await fetch(`/api/prowadzacy/termin/${id}`, {
                    credentials: 'include',
                    headers: { ...authHeader() },
                });
                if (g.ok) finalTerm = await g.json();
            }

            if (!finalTerm || !finalTerm.exam_datetime) {
                setOkMsg('Zapisano zmiany (odświeżenie szczegółów może potrwać chwilę).');
                return;
            }

            setTerm(prev => ({ ...prev, ...finalTerm }));
            setForm(f => ({
                ...f,
                exam_id: Number(finalTerm.exam_id ?? f.exam_id),
                group_id: Number(finalTerm.group_id ?? f.group_id),
                exam_datetime: toLocalInputValue(finalTerm.exam_datetime),
                exam_duration: Number(finalTerm.exam_duration ?? f.exam_duration),
                proctoring: finalTerm.proctoring ? 1 : 0,
                level: finalTerm.level || f.level || 'brak',
            }));

            setOkMsg('Zapisano zmiany.');
        } catch (e2) {
            setErr(e2.message || 'Błąd zapisu');
        } finally {
            setSaving(false);
        }
    }

    async function onDelete() {
        setErr(''); setOkMsg('');
        setDeleting(true);

        const examIdForUrl = term?.exam_id ?? form?.exam_id ?? null;
        const targetUrl = examIdForUrl
            ? `/prowadzacy/egzamin/${examIdForUrl}/terminy`
            : '/prowadzacy/egzaminy';

        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 10000);

        try {
            console.log('DELETE start', { id, targetUrl });
            const r = await fetch(`/api/prowadzacy/termin/${id}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: { ...authHeader() },
                signal: controller.signal,
            });
            clearTimeout(t);

            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j?.blad || j?.error || `Błąd usuwania (${r.status})`);
            }

            console.log('DELETE ok → nav', targetUrl);
            nav(targetUrl, { replace: true });
        } catch (e) {
            if (e.name === 'AbortError') {
                setErr('Przekroczono czas oczekiwania na odpowiedź serwera (CORS / sieć).');
            } else {
                setErr(e.message || 'Błąd usuwania');
            }
        } finally {
            clearTimeout(t);
            setDeleting(false);
            setShowConfirm(false);
            setConfirmText('');
        }
    }

    async function copyCode() {
        try {
            if (!joinCode) return;
            await navigator.clipboard.writeText(joinCode);
            setCopyOk('Skopiowano kod.');
            setTimeout(() => setCopyOk(''), 2000);
        } catch { setCopyOk(''); }
    }

    if (loading) return <div style={{ padding: 16, marginTop: 50 }}>Ładowanie…</div>;
    if (!term) return <div style={{ padding: 16, marginTop: 50, color: '#a00' }}>{err || 'Nie znaleziono terminu'}</div>;

    return (
        <div style={{ maxWidth: 900, marginTop: 80 , padding: '0 16px' }}>
            <h1 style={{ marginBottom: 12 }}>Szczegóły terminu</h1>

            {/* KOD DOŁĄCZENIA */}
            <div style={{ border:'1px solid #e5e7eb', borderRadius:12, padding:16, background:'#fff', marginBottom:12 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Kod dołączenia</div>
                {joinErr ? (
                    <div style={{ color:'#b91c1c' }}>{joinErr}</div>
                ) : (
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                        <code style={{ fontSize:18 }}>{joinCode || '—'}</code>
                        <button onClick={copyCode} className={'but-rozwin'} disabled={!joinCode}>Kopiuj</button>
                        {copyOk && <span style={{ color:'#059669' }}>{copyOk}</span>}
                    </div>
                )}
                <div style={{ marginTop:6, color:'#6b7280', fontSize:13 }}>
                    Udostępnij ten kod studentom spoza grupy— po wpisaniu dołączą do terminu jako goście.
                </div>
            </div>

            <div style={{ marginBottom: 12, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', backgroundColor: '#fff', padding:16 }}>
                <div><b>Egzamin:</b> {term.title}</div>
                <div><b>Grupa:</b> {term.group_name}</div>
                <div><b>Kierunek:</b> {term.field_of_study}</div>
                <div><b>Semestr:</b> {term.study_semester}</div>
                <div><b>Tryb:</b> {term.study_mode}</div>
                <div><b>Start:</b> {formatPL(term.exam_datetime)}</div>
                <div><b>Czas:</b> {term.exam_duration} min</div>
                <div><b>Proctoring:</b> {term.proctoring ? 'tak' : 'nie'}</div>
                <div><b>Poziom bezpieczeństwa (przeglądarka SEB):</b> {term.level || 'brak'}</div>
            </div>

            {isLocked ? (
                <div style={{ padding: 12, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, marginBottom: 12 }}>
                    <b>Edycja zablokowana.</b> Do rozpoczęcia egzaminu pozostało ≤ 15 minut (lub egzamin już się rozpoczął).
                </div>
            ) : (
                <div style={{ padding: 12, background: '#ecfeff', border: '1px solid #67e8f9', borderRadius: 8, marginBottom: 12 }}>
                    <b>Edycja dostępna.</b> Edycja i usuwanie są możliwe do 15 minut przed startem.
                </div>
            )}

            <div style={{ marginBottom: 8, color: '#6b7280' }}>
                Czas do startu: {leftToStart ?? '—'}
            </div>

            <form onSubmit={onSave}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'grid', gap: 12, background: 'white' }}>
                {/* Tytuł do podglądu (nie wysyłamy) */}
                <div style={{ display: 'grid', gap: 6 }}>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Tytuł egzaminu</label>
                    <input type="text" value={form.title} disabled readOnly />
                </div>

                <div style={{ display: 'grid', gap: 6 }}>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Data i godzina (czas lokalny)</label>
                    <input
                        type="datetime-local"
                        value={form.exam_datetime}
                        disabled={isLocked}
                        onChange={(e) => setField('exam_datetime', e.target.value)}
                        required
                    />
                </div>

                <div style={{ display: 'grid', gap: 6 }}>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Czas trwania (minuty)</label>
                    <input
                        type="number"
                        min={1}
                        value={form.exam_duration}
                        disabled={isLocked}
                        onChange={(e) => setField('exam_duration', Number(e.target.value))}
                        required
                    />
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            type="checkbox"
                            checked={!!form.proctoring}
                            disabled={isLocked}
                            onChange={(e) => setField('proctoring', e.target.checked ? 1 : 0)}
                        />
                        <span>Włącz proctoring (kamera/mikrofon + flagi)</span>
                    </label>
                </div>

                <div style={{ display: 'grid', gap: 6 }}>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Poziom bezpieczeństwa (przeglądarka SEB)</label>
                    <select
                        value={form.level || 'brak'}
                        disabled={isLocked}
                        onChange={(e) => setField('level', e.target.value)}
                    >
                        <option value="brak">Brak</option>
                        <option value="niski">Niski</option>
                        <option value="umiarkowany">Umiarkowany</option>
                        <option value="wysoki">Wysoki</option>
                    </select>
                    <div style={{ color:'#475569', fontSize:13, lineHeight:1.45 }}>
                        <div><b>Niski / umiarkowany / wysoki</b> - wymaga SEB.</div>
                        <div><b>Brak</b> - SEB nie jest wymagany, a aplikacja zapisuje listę naruszeń po podejściu.</div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="submit" className={'but-smal'} disabled={isLocked || saving}>
                        {saving ? 'Zapisywanie…' : 'Zapisz zmiany'}
                    </button>

                    {!isLocked && (
                        <button
                            type="button"
                            className={'but-smal'}
                            onClick={() => setShowConfirm(true)}
                            disabled={deleting}
                            style={{ background: 'red', border: '1px solid #fecaca' }}
                        >
                            Usuń termin
                        </button>
                    )}

                    {canGoToRun && (
                        <button
                            type="button"
                            className={'but-smal'}
                            onClick={() => nav(`/prowadzacy/termin/${id}/przebieg`)}
                            style={{ marginLeft: 'auto' }}
                        >
                            Przejdź do przebiegu terminu
                        </button>
                    )}
                </div>

                {err && <div style={{ color: 'crimson' }}>{err}</div>}
                {okMsg && <div style={{ color: '#166534' }}>{okMsg}</div>}
            </form>

            {/* Modal potwierdzenia usunięcia */}
            {showConfirm && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)',
                        display: 'grid', placeItems: 'center', padding: 16, zIndex: 50
                    }}
                >
                    <div style={{ background: '#fff', borderRadius: 12, padding: 16, width: 'min(520px, 95vw)', boxShadow: '0 10px 30px rgba(0,0,0,.15)' }}>
                        <h3 style={{ marginTop: 0 }}>Potwierdź usunięcie terminu</h3>
                        <p>
                            Tej operacji nie można cofnąć. Aby potwierdzić, wpisz <b>tak</b> w polu poniżej
                            i kliknij „Usuń”.
                        </p>
                        <input
                            autoFocus
                            placeholder="wpisz: tak"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            style={{ width: '100%', margin: '8px 0 12px' }}
                        />
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => { setShowConfirm(false); setConfirmText(''); }}>Anuluj</button>
                            <button
                                className={'but-smal'}
                                disabled={confirmText.trim().toLowerCase() !== 'tak' || deleting}
                                onClick={onDelete}
                                style={{ background: '#dc2626', color: '#fff', border: 'none' }}
                            >
                                {deleting ? 'Usuwanie…' : 'Usuń'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
